/**
 * Double displacement between two aqueous salts: which products could form, whether either
 * is insoluble, and the balanced molecular, complete ionic and net ionic equations.
 *
 *     AX(aq) + BY(aq) → AY + BX
 *
 * Coefficients are found by brute force over small whole numbers; ion lists are built
 * from the salts' ion counts so spectators cancel exactly.
 */

import {
  getIon,
  type Ion,
  makeSalt,
  type Salt,
  saltFormula,
} from "@/lib/tool-kit/chem-reference/ions";
import {
  classifySalt,
  type Status,
  type Verdict,
} from "@/lib/tool-kit/chem-reference/solubility-rules";

export interface Product {
  salt: Salt;
  formula: string;
  verdict: Verdict;
  /** True when this product is written as a solid. */
  solid: boolean;
}

export interface Coefficients {
  r1: number;
  r2: number;
  p1: number;
  p2: number;
}

const MAX_COEFFICIENT = 12;

/** Smallest whole-number coefficients that balance every ion; null if none (should not happen). */
export function balanceCoefficients(
  s1: Salt,
  s2: Salt,
  p1: Salt,
  p2: Salt
): Coefficients | null {
  // p1 = cation of s1 with anion of s2; p2 = cation of s2 with anion of s1.
  const balanced = (r1: number, r2: number, q1: number, q2: number) =>
    r1 * s1.nCation === q1 * p1.nCation &&
    r2 * s2.nAnion === q1 * p1.nAnion &&
    r2 * s2.nCation === q2 * p2.nCation &&
    r1 * s1.nAnion === q2 * p2.nAnion;
  const range = Array.from({ length: MAX_COEFFICIENT }, (_, i) => i + 1);
  let best: (Coefficients & { total: number }) | null = null;
  for (const r1 of range) {
    for (const r2 of range) {
      for (const q1 of range) {
        for (const q2 of range) {
          const total = r1 + r2 + q1 + q2;
          if (balanced(r1, r2, q1, q2) && (!best || total < best.total)) {
            best = { r1, r2, p1: q1, p2: q2, total };
          }
        }
      }
    }
  }
  return best ? { r1: best.r1, r2: best.r2, p1: best.p1, p2: best.p2 } : null;
}

const SOLID: ReadonlySet<Status> = new Set(["insoluble", "slightly"]);

function product(cation: Ion, anion: Ion): Product {
  const salt = makeSalt(cation, anion);
  const verdict = classifySalt(salt);
  return {
    salt,
    formula: saltFormula(salt),
    verdict,
    solid: SOLID.has(verdict.status),
  };
}

export type MixProblem = "sameCation" | "sameAnion";

export interface Mixture {
  reactants: [Salt, Salt];
  products: [Product, Product];
  coefficients: Coefficients;
  precipitates: Product[];
}

/** The products of mixing two salts, or a reason it is not a double displacement. */
export function mix(s1: Salt, s2: Salt): Mixture | { problem: MixProblem } {
  if (s1.cation.id === s2.cation.id) {
    return { problem: "sameCation" };
  }
  if (s1.anion.id === s2.anion.id) {
    return { problem: "sameAnion" };
  }
  const first = product(s1.cation, s2.anion);
  const second = product(s2.cation, s1.anion);
  const coefficients = balanceCoefficients(s1, s2, first.salt, second.salt);
  if (!coefficients) {
    return { problem: "sameCation" };
  }
  return {
    reactants: [s1, s2],
    products: [first, second],
    coefficients,
    precipitates: [first, second].filter((p) => p.solid),
  };
}

/* --------------------------------------------------------------- equations */

export interface Term {
  coefficient: number;
  /** "Pb²⁺(aq)" or "PbI₂(s)" without the coefficient. */
  text: string;
}

export interface Equations {
  molecular: { left: Term[]; right: Term[] };
  complete: { left: Term[]; right: Term[] };
  /** Empty when nothing precipitates. */
  net: { left: Term[]; right: Term[] } | null;
}

const withState = (formula: string, state: string) => `${formula}(${state})`;

function addIons(map: Map<string, number>, salt: Salt, times: number) {
  const cation = withState(salt.cation.text, "aq");
  const anion = withState(salt.anion.text, "aq");
  map.set(cation, (map.get(cation) ?? 0) + times * salt.nCation);
  map.set(anion, (map.get(anion) ?? 0) + times * salt.nAnion);
}

const toTerms = (map: Map<string, number>): Term[] =>
  [...map]
    .filter(([, c]) => c > 0)
    .map(([text, coefficient]) => ({ text, coefficient }));

/** Cations before anions, keeping the order within each. */
const cationsFirst = (terms: Term[]): Term[] => [
  ...terms.filter((t) => t.text.includes("⁺")),
  ...terms.filter((t) => !t.text.includes("⁺")),
];

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

function reduce(side: { left: Term[]; right: Term[] }) {
  const all = [...side.left, ...side.right].map((t) => t.coefficient);
  const divisor = all.reduce(gcd, 0) || 1;
  return {
    left: side.left.map((t) => ({
      ...t,
      coefficient: t.coefficient / divisor,
    })),
    right: side.right.map((t) => ({
      ...t,
      coefficient: t.coefficient / divisor,
    })),
  };
}

export function equations(mixture: Mixture): Equations {
  const { reactants, products, coefficients: c } = mixture;
  const [s1, s2] = reactants;
  const molecular = {
    left: [
      { coefficient: c.r1, text: withState(saltFormula(s1), "aq") },
      { coefficient: c.r2, text: withState(saltFormula(s2), "aq") },
    ],
    right: [
      {
        coefficient: c.p1,
        text: withState(products[0].formula, products[0].solid ? "s" : "aq"),
      },
      {
        coefficient: c.p2,
        text: withState(products[1].formula, products[1].solid ? "s" : "aq"),
      },
    ],
  };
  const leftIons = new Map<string, number>();
  addIons(leftIons, s1, c.r1);
  addIons(leftIons, s2, c.r2);
  const rightIons = new Map<string, number>();
  const rightSolids: Term[] = [];
  [products[0], products[1]].forEach((p, index) => {
    const times = index === 0 ? c.p1 : c.p2;
    if (p.solid) {
      rightSolids.push({ coefficient: times, text: withState(p.formula, "s") });
    } else {
      addIons(rightIons, p.salt, times);
    }
  });
  const complete = {
    left: toTerms(leftIons),
    right: [...toTerms(rightIons), ...rightSolids],
  };
  if (rightSolids.length === 0) {
    return { molecular, complete, net: null };
  }
  // Cancel spectators: what is left of each ion after the smaller side is removed.
  const netLeft = new Map<string, number>();
  const netRight = new Map<string, number>();
  for (const [text, count] of leftIons) {
    const other = rightIons.get(text) ?? 0;
    netLeft.set(text, count - Math.min(count, other));
  }
  for (const [text, count] of rightIons) {
    const other = leftIons.get(text) ?? 0;
    netRight.set(text, count - Math.min(count, other));
  }
  const net = reduce({
    left: cationsFirst(toTerms(netLeft)),
    right: [...cationsFirst(toTerms(netRight)), ...rightSolids],
  });
  return { molecular, complete, net };
}

/** Both sides written as a string: "Pb²⁺(aq) + 2 I⁻(aq) → PbI₂(s)". */
export function writeEquation(side: { left: Term[]; right: Term[] }): string {
  const text = (terms: Term[]) =>
    terms
      .map((t) => (t.coefficient === 1 ? t.text : `${t.coefficient} ${t.text}`))
      .join(" + ");
  return `${text(side.left)} → ${text(side.right)}`;
}

export const ionOf = getIon;

/* ------------------------------------------------------ quantitative check */

export interface Dilution {
  /** Concentration of each ion just after mixing, mol/L. */
  cation: number;
  anion: number;
}

/**
 * Ion concentrations of a precipitate's ions right after mixing volumes v1 and v2 (any
 * one unit) of solutions at c1 and c2 (mol/L), before anything precipitates.
 */
export function afterMixing(
  precipitate: Product,
  mixture: Mixture,
  c1: number,
  v1: number,
  c2: number,
  v2: number
): Dilution {
  const [s1, s2] = mixture.reactants;
  const volume = v1 + v2;
  const fromFirst = (c1 * v1) / volume;
  const fromSecond = (c2 * v2) / volume;
  const p = precipitate.salt;
  // The precipitate's cation comes from s1 when it is s1's cation, otherwise from s2.
  const cation =
    p.cation.id === s1.cation.id
      ? fromFirst * s1.nCation
      : fromSecond * s2.nCation;
  const anion =
    p.anion.id === s2.anion.id ? fromSecond * s2.nAnion : fromFirst * s1.nAnion;
  return { cation, anion };
}
